import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Box,
  Button,
  Typography,
  TextField,
  MenuItem,
  Alert,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  Checkbox,
  FormControlLabel,
  Chip
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Description as PdfIcon } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import eventService from '../../services/eventService';

// Validation schema
const schema = yup.object({
  eventName: yup.string().required('Event name is required').max(200, 'Event name too long'),
  description: yup.string().required('Description is required').max(5000, 'Description too long'),
  category: yup.string().required('Category is required'),
  venue: yup.string().required('Venue is required'),
  address: yup.string().required('Address is required'),
  city: yup.string().required('City is required'),
  state: yup.string().required('State is required'),
  startDate: yup.string().required('Start date is required'),
  startTime: yup.string().required('Start time is required'),
  endDate: yup.string(),
  endTime: yup.string(),
  capacity: yup.number().min(0, 'Capacity must be positive').nullable(),
  rsvpDeadline: yup.string(),
  status: yup.string().oneOf(['draft', 'published']).required()
}).required();

const CreateEvent = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [bannerFile, setBannerFile] = useState(null);
  const [bannerPreview, setBannerPreview] = useState('');
  const [brochureFile, setBrochureFile] = useState(null);
  const [customFields, setCustomFields] = useState([]);

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onBlur',
    defaultValues: {
      eventName: '',
      description: '',
      category: 'General',
      venue: '',
      address: '',
      city: '',
      state: '',
      country: 'USA',
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      capacity: 0,
      rsvpEnabled: true,
      rsvpDeadline: '',
      requireApproval: false,
      status: 'draft',
      tags: []
    }
  });

  const handleBannerChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }

      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }

      setBannerFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerPreview(reader.result);
      };
      reader.readAsDataURL(file);
      setError('');
    }
  };

  const handleBrochureChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setError('PDF size must be less than 10MB');
        return;
      }

      if (file.type !== 'application/pdf') {
        setError('Please select a PDF file');
        return;
      }

      setBrochureFile(file);
      setError('');
    }
  };

  const addCustomField = () => {
    setCustomFields([...customFields, {
      id: Date.now(),
      fieldName: '',
      fieldLabel: '',
      fieldType: 'text',
      required: false,
      placeholder: '',
      options: [],
      order: customFields.length
    }]);
  };

  const removeCustomField = (id) => {
    setCustomFields(customFields.filter(field => field.id !== id));
  };

  const updateCustomField = (id, key, value) => {
    setCustomFields(customFields.map(field =>
      field.id === id ? { ...field, [key]: value } : field
    ));
  };

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      setError('');

      // Prepare custom fields - remove temporary id and clean up
      const cleanedCustomFields = customFields.map(({ id, ...field }) => ({
        fieldName: (field.fieldLabel || '').trim().toLowerCase().replace(/\s+/g, '_'),
        fieldLabel: field.fieldLabel,
        fieldType: field.fieldType,
        required: field.required,
        placeholder: field.placeholder,
        ...(field.fieldType === 'select' && { options: field.options }),
        order: field.order
      })).filter(field => field.fieldName && field.fieldLabel);

      // Prepare event data
      const eventData = {
        ...data,
        location: {
          venue: data.venue,
          address: data.address,
          city: data.city,
          state: data.state,
          country: data.country || 'USA'
        },
        dates: [{
          startDate: data.startDate,
          endDate: data.endDate || data.startDate,
          startTime: data.startTime,
          endTime: data.endTime || data.startTime
        }],
        customFields: cleanedCustomFields,
        banner: bannerFile,
        brochure: brochureFile
      };

      // Remove individual location fields
      delete eventData.venue;
      delete eventData.address;
      delete eventData.city;
      delete eventData.state;
      delete eventData.country;
      delete eventData.startDate;
      delete eventData.endDate;
      delete eventData.startTime;
      delete eventData.endTime;

      const response = await eventService.createEvent(eventData);

      if (response.success) {
        navigate('/admin/events');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create event');
      setLoading(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            Create New Event
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Fill in the details below to create a new event
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit(onSubmit)}>
          {/* Banner Upload Section */}
          <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Event Banner
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload a banner image for your event (Optional, max 5MB)
              </Typography>

              <input
                accept="image/*"
                style={{ display: 'none' }}
                id="banner-upload"
                type="file"
                onChange={handleBannerChange}
              />
              <label htmlFor="banner-upload">
                <Button
                  variant="contained"
                  component="span"
                  size="large"
                >
                  Choose Banner Image
                </Button>
              </label>

              {bannerPreview && (
                <Box
                  mt={3}
                  sx={{
                    border: '2px dashed',
                    borderColor: 'primary.main',
                    borderRadius: 2,
                    p: 2,
                    bgcolor: 'white'
                  }}
                >
                  <img
                    src={bannerPreview}
                    alt="Banner preview"
                    style={{
                      width: '100%',
                      maxHeight: '300px',
                      objectFit: 'contain',
                      borderRadius: '8px'
                    }}
                  />
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Brochure Upload Section */}
          <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Event Brochure (Optional)
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Upload a PDF brochure that will be sent to attendees in confirmation emails (Max 10MB)
              </Typography>

              <input
                accept="application/pdf"
                style={{ display: 'none' }}
                id="brochure-upload"
                type="file"
                onChange={handleBrochureChange}
              />
              <label htmlFor="brochure-upload">
                <Button
                  variant="outlined"
                  component="span"
                  size="large"
                  startIcon={<PdfIcon />}
                >
                  Choose PDF Brochure
                </Button>
              </label>

              {brochureFile && (
                <Box mt={2} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Chip
                    icon={<PdfIcon />}
                    label={brochureFile.name}
                    onDelete={() => setBrochureFile(null)}
                    color="primary"
                    variant="outlined"
                  />
                  <Typography variant="caption" color="text.secondary">
                    ({(brochureFile.size / 1024 / 1024).toFixed(2)} MB)
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Event Details Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Event Details
              </Typography>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Controller
                  name="eventName"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Event Name"
                      placeholder="Enter event name"
                      error={!!errors.eventName}
                      helperText={errors.eventName?.message}
                      required
                    />
                  )}
                />

                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      rows={5}
                      label="Event Description"
                      placeholder="Describe your event in detail..."
                      error={!!errors.description}
                      helperText={errors.description?.message}
                      required
                    />
                  )}
                />

                <Controller
                  name="category"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      select
                      label="Category"
                      error={!!errors.category}
                      helperText={errors.category?.message}
                      required
                    >
                      <MenuItem value="General">General</MenuItem>
                      <MenuItem value="Conference">Conference</MenuItem>
                      <MenuItem value="Workshop">Workshop</MenuItem>
                      <MenuItem value="Seminar">Seminar</MenuItem>
                      <MenuItem value="Meetup">Meetup</MenuItem>
                      <MenuItem value="Social">Social</MenuItem>
                      <MenuItem value="Networking">Networking</MenuItem>
                      <MenuItem value="Training">Training</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </TextField>
                  )}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Location Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Event Location
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                  gap: 3,
                }}
              >
                <Controller
                  name="venue"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Venue Name"
                      placeholder="e.g., Conference Center"
                      error={!!errors.venue}
                      helperText={errors.venue?.message}
                      required
                    />
                  )}
                />

                <Controller
                  name="address"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Street Address"
                      placeholder="e.g., 123 Main Street"
                      error={!!errors.address}
                      helperText={errors.address?.message}
                      required
                    />
                  )}
                />

                <Controller
                  name="city"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="City"
                      placeholder="e.g., New York"
                      error={!!errors.city}
                      helperText={errors.city?.message}
                      required
                    />
                  )}
                />

                <Controller
                  name="state"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="State / Province"
                      placeholder="e.g., NY"
                      error={!!errors.state}
                      helperText={errors.state?.message}
                      required
                    />
                  )}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Schedule Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Event Schedule
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                  gap: 3,
                }}
              >
                <Controller
                  name="startDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="date"
                      label="Start Date"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: today }}
                      error={!!errors.startDate}
                      helperText={errors.startDate?.message}
                      required
                    />
                  )}
                />

                <Controller
                  name="startTime"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="time"
                      label="Start Time"
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.startTime}
                      helperText={errors.startTime?.message}
                      required
                    />
                  )}
                />

                <Controller
                  name="endDate"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="date"
                      label="End Date (Optional)"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: today }}
                      error={!!errors.endDate}
                      helperText={errors.endDate?.message || 'Leave blank if same as start date'}
                    />
                  )}
                />

                <Controller
                  name="endTime"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="time"
                      label="End Time (Optional)"
                      InputLabelProps={{ shrink: true }}
                      error={!!errors.endTime}
                      helperText={errors.endTime?.message || 'Leave blank if same as start time'}
                    />
                  )}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Event Settings Section */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
                Event Settings
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
                  gap: 3,
                }}
              >
                <Controller
                  name="capacity"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="number"
                      label="Maximum Capacity"
                      placeholder="0 for unlimited"
                      error={!!errors.capacity}
                      helperText={errors.capacity?.message || 'Enter 0 for unlimited capacity'}
                      inputProps={{ min: 0 }}
                    />
                  )}
                />

                <Controller
                  name="rsvpDeadline"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      type="date"
                      label="RSVP Deadline (Optional)"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: today }}
                      error={!!errors.rsvpDeadline}
                      helperText={errors.rsvpDeadline?.message || 'Last date for RSVPs'}
                    />
                  )}
                />

                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      select
                      label="Publish Status"
                      error={!!errors.status}
                      helperText={errors.status?.message || 'Choose whether to publish now or save as draft'}
                      required
                    >
                      <MenuItem value="draft">Save as Draft</MenuItem>
                      <MenuItem value="published">Publish Immediately</MenuItem>
                    </TextField>
                  )}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Custom RSVP Fields Builder */}
          <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6" gutterBottom>
                    Custom RSVP Fields (Optional)
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add custom fields that attendees must fill when registering
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={addCustomField}
                  size="small"
                >
                  Add Field
                </Button>
              </Box>

              {customFields.length > 0 && (
                <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {customFields.map((field, index) => (
                    <Card key={field.id} variant="outlined" sx={{ bgcolor: 'background.paper' }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Typography variant="subtitle2" color="primary">
                            Field {index + 1}
                          </Typography>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeCustomField(field.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                            <TextField
                              fullWidth
                              label="Field Label"
                              placeholder="e.g., Clinic Name"
                              value={field.fieldLabel}
                              onChange={(e) => updateCustomField(field.id, 'fieldLabel', e.target.value)}
                              size="small"
                            />
                            <FormControl fullWidth size="small">
                              <InputLabel>Field Type</InputLabel>
                              <Select
                                value={field.fieldType}
                                label="Field Type"
                                onChange={(e) => updateCustomField(field.id, 'fieldType', e.target.value)}
                              >
                                <MenuItem value="text">Text</MenuItem>
                                <MenuItem value="email">Email</MenuItem>
                                <MenuItem value="phone">Phone</MenuItem>
                                <MenuItem value="number">Number</MenuItem>
                                <MenuItem value="textarea">Textarea</MenuItem>
                                <MenuItem value="select">Dropdown</MenuItem>
                              </Select>
                            </FormControl>
                          </Box>

                          <TextField
                            fullWidth
                            label="Placeholder Text (Optional)"
                            placeholder="e.g., Enter your clinic name"
                            value={field.placeholder}
                            onChange={(e) => updateCustomField(field.id, 'placeholder', e.target.value)}
                            size="small"
                          />

                          {field.fieldType === 'select' && (
                            <TextField
                              fullWidth
                              label="Options (comma-separated)"
                              placeholder="e.g., Option 1, Option 2, Option 3"
                              value={field.options.join(', ')}
                              onChange={(e) => updateCustomField(field.id, 'options', e.target.value.split(',').map(o => o.trim()))}
                              size="small"
                              helperText="Separate options with commas"
                            />
                          )}

                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={field.required}
                                onChange={(e) => updateCustomField(field.id, 'required', e.target.checked)}
                              />
                            }
                            label="Required field"
                          />
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}

              {customFields.length === 0 && (
                <Box
                  sx={{
                    mt: 2,
                    p: 3,
                    textAlign: 'center',
                    border: '2px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'background.paper'
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    No custom fields added yet. Click "Add Field" to create custom RSVP fields.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          <Divider sx={{ my: 3 }} />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={() => navigate('/admin/events')}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
              sx={{ minWidth: 150 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Create Event'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Container>
  );
};

export default CreateEvent;
